import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemButton,
  ListItemText,
  IconButton,
  styled,
  useMediaQuery,
  useTheme,
  AppBar,
  Toolbar,
  FormControl,
  InputLabel,
  Select,
  Divider,
  MenuItem,
  TextField,
  InputAdornment,
  Grid,
} from '@mui/material';

import { 
  People as ClientsIcon,
  Code as CodesIcon,
  ShoppingBasket as ProductsIcon,
  Search as SearchIcon,
  Receipt as ChecksIcon,
  LocationOn as AddressesIcon
} from '@mui/icons-material';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MailIcon from '@mui/icons-material/Mail';
import ReviewsIcon from '@mui/icons-material/Reviews';
import AddClientModal from '../components/AddClientModal';
import ClientList from '../components/ClientList';
import Sidebar from '../components/Sidebar';
import ProductsList from '../components/ProductsList';
import AddProductModal from '../components/AddProductModal';
import CodesManager from '../components/CodesManager';
import ChecksList from '../components/ChecksList';
import { fetchData, fetchProducts, deleteProduct } from '../api/github';
import { formatDistanceToNow } from 'date-fns';
import MailList from '../components/MailList';
import FloatingActionButton from '../components/FloatingActionButton';
import LoadingAnimation from '../components/LoadingAnimation';
import MailPhotoList from '../components/MailPhotoList';
import ReviewsList from '../components/ReviewsList';
import TemplatesList from '../components/TemplatesList';
import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft';
import AddressesList from '../components/AddressesList';


const MainContent = styled(Box)(({ theme }) => ({
  marginLeft: 250,
  width: `calc(100% - 250px)`,
  padding: theme.spacing(3),
  minHeight: '100vh',
}));

const sections = [
  { id: 'clients', label: 'Мижозлар', icon: <ClientsIcon /> },
  { id: 'products', label: 'Махсулотлар', icon: <ProductsIcon /> },
  { id: 'codes', label: 'Кодлар', icon: <CodesIcon /> },
  { id: 'mail', label: 'Почта', icon: <MailIcon /> },
  { id: 'checks', label: 'Чеклар', icon: <ChecksIcon /> },
  { id: 'mailPhoto', label: 'Почта Расм', icon: <ChecksIcon /> },
  { id: 'reviews', label: 'Отзывы', icon: <ReviewsIcon /> },
  { id: 'templates', label: 'Шаблоны', icon: <FormatAlignLeftIcon /> },
  { id: 'addresses', label: 'Манзиллар', icon: <AddressesIcon /> }
];

export default function EmployeeDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [modalOpen, setModalOpen] = useState(false);
  const employeeId = localStorage.getItem('userId');
  const [activeSection, setActiveSection] = useState(null); // null = список разделов
  const [lastLogin, setLastLogin] = useState('');
  const userId = localStorage.getItem('userId');
  const [products, setProducts] = useState([]);
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productFilterField, setProductFilterField] = useState('all');
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Управление историей для кнопки "Назад"
  useEffect(() => {
    // При смене activeSection добавляем запись в историю
    if (activeSection) {
      window.history.pushState({ section: activeSection }, '', `#${activeSection}`);
    } else {
      // Для главного меню заменяем текущую запись
      window.history.replaceState({ section: null }, '', '#home');
    }

    // Обработчик события popstate для аппаратной кнопки "Назад"
    const handlePopState = (event) => {
      event.preventDefault();
      if (activeSection) {
        // Если в разделе, возвращаемся в главное меню
        setActiveSection(null);
        // Заменяем текущую запись в истории, чтобы избежать дублирования
        window.history.replaceState({ section: null }, '', '#home');
      } else {
        // Если в главном меню, можно разрешить выход (или ничего не делать)
        // Если хочешь запретить выход, убери window.history.back()
        // window.history.back();
      }
    };

    window.addEventListener('popstate', handlePopState);

    // Очистка обработчика при размонтировании
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [activeSection]);

  const handleDeleteProduct = async (id) => {
    try {
      await deleteProduct(id);
      await loadProducts();
    } catch (error) {
      console.error("Ошибка при удалении:", error);
    }
  };

  const handleModalClose = (shouldRefresh) => {
    if (shouldRefresh) {
      setRefreshTrigger(prev => prev + 1);
    }
    setModalOpen(false);
    setProductModalOpen(false);
  };

  const loadProducts = async () => {
    setLoading(true);
    try {
      const data = await fetchProducts();
      setProducts(data);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(product => {
    return productFilterField === 'name' && product.name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
           productFilterField === 'all' && product.name.toLowerCase().includes(productSearchTerm.toLowerCase()) ||
           productFilterField === 'price' && product.price.toString().includes(productSearchTerm) ||
           productFilterField === 'stock' && product.stock.toString().includes(productSearchTerm);
  });

  useEffect(() => {
    if (activeSection === 'products') loadProducts();
  }, [activeSection, refreshTrigger]);

  useEffect(() => {
    const loadLastLogin = async () => {
      try {
        const employees = await fetchData('employees.json');
        const currentUser = employees.find(e => e.id === userId);
        if (currentUser && currentUser.lastLogin) {
          setLastLogin(formatDistanceToNow(new Date(currentUser.lastLogin), { addSuffix: true }));
        }
      } catch (error) {
        console.error("Ошибка загрузки данных о последнем входе:", error);
      }
    };
    loadLastLogin();
  }, [userId]);

  // Обработчик возврата к списку разделов
  const handleBackToList = () => {
    setActiveSection(null);
    // Обновляем историю, чтобы отражать главное меню
    window.history.replaceState({ section: null }, '', '#home');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row' }}>
      <Sidebar showMenuButton={!activeSection} />

      <MainContent sx={{
        marginLeft: 0,
        width: isMobile ? '100%' : 'calc(100% - 250px)',
        padding: isMobile ? 0 : 1
      }}>
        <Container maxWidth="lg" sx={{ px: isMobile ? 0 : 2 }}>
          {/* Если выбран раздел — показываем его с кнопкой "Назад" */}
          {activeSection ? (
            <Box>
              {/* Шапка с кнопкой "Назад" */}
              <AppBar 
                position="fixed" 
                color="transparent" 
                elevation={0}
                sx={{ mb: 2, bgcolor: 'background.paper', color: 'white'}}
              >
                <Toolbar sx={{ px: 0, color: 'white' }}>
                  <IconButton onClick={handleBackToList} sx={{color: 'white'}}>
                    <ArrowBackIcon />
                  </IconButton>
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {sections.find(s => s.id === activeSection)?.label}
                  </Typography>
                </Toolbar>
              </AppBar>

              <Box sx={{padding: 2}}>
                {/* Контент раздела */}
                {activeSection === 'clients' ? (
                  <>
                    <Divider sx={{ mb: 10 }} />
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'flex-end',
                      mb: 0
                    }}>
                      <FloatingActionButton 
                        onClick={() => setModalOpen(true)}
                        tooltip="Мижоз қўшиш"
                      />
                    </Box>

                    <AddClientModal 
                      open={modalOpen}
                      onClose={handleModalClose}
                      employeeId={employeeId}
                    />

                    <ClientList isMobile={isMobile} refreshTrigger={refreshTrigger} />
                  </>
                ) : activeSection === 'products' ? (loading ? (
                  <LoadingAnimation loading={loading} />
                ) : (
                  <Box>
                    <Divider sx={{ mb: 10 }} />
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: isMobile ? 'flex-start' : 'center',
                      mb: 0,
                      gap: 0
                    }}>
                      <FloatingActionButton 
                        onClick={() => { setEditingProduct(null); setProductModalOpen(true); }}
                        tooltip="Махсулот қўшиш"
                      />
                    </Box>

                    <Box sx={{ mb: 2, display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2 }}>
                      <FormControl sx={{ minWidth: 120 }}>
                        <InputLabel>Фильтр по</InputLabel>
                        <Select
                          value={productFilterField}
                          onChange={(e) => setProductFilterField(e.target.value)}
                          label="Фильтр по"
                        >
                          <MenuItem value="all">Хаммаси</MenuItem>
                          <MenuItem value="name">Номи</MenuItem>
                          <MenuItem value="price">Нархи</MenuItem>
                          <MenuItem value="stock">Қолдиқ</MenuItem>
                        </Select>
                      </FormControl>

                      <TextField
                        placeholder="Махсулот қидириш..."
                        value={productSearchTerm}
                        onChange={(e) => setProductSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                        fullWidth
                      />
                    </Box>

                    <Grid container spacing={2} sx={{display: 'flex', justifyContent: 'space-evenly'}}>
                      {filteredProducts.map(product => (
                        <Grid key={product.id}>
                          <ProductsList
                            product={product}
                            onEdit={(product) => { setEditingProduct(product); setProductModalOpen(true); }}
                            onDelete={handleDeleteProduct}
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )) : activeSection === 'mail' ? (
                  <Box>
                    <Divider sx={{ mb: 10 }} />
                    <MailList isMobile={isMobile} refreshTrigger={refreshTrigger} />
                  </Box>
                ) : activeSection === 'checks' ? (
                  <Box>
                    <Divider sx={{ mb: 10 }} />
                    <ChecksList isMobile={isMobile} refreshTrigger={refreshTrigger} />
                  </Box>
                ) : activeSection === 'mailPhoto' ? (
                  <Box>
                    <Divider sx={{ mb: 10 }} />
                    <MailPhotoList isMobile={isMobile} refreshTrigger={refreshTrigger} />
                  </Box>
                ) : activeSection === 'reviews' ? (
                  <Box>
                    <Divider sx={{ mb: 10 }} />
                    <ReviewsList isMobile={isMobile} refreshTrigger={refreshTrigger} />
                  </Box>
                ) : activeSection === 'templates' ? (
                  <Box>
                    <Divider sx={{ mb: 10 }} />
                    <TemplatesList isMobile={isMobile} refreshTrigger={refreshTrigger} />
                  </Box>
                ) : activeSection === 'addresses' ? (
                  <Box>
                    <Divider sx={{ mb: 10 }} />
                    <AddressesList isMobile={isMobile} refreshTrigger={refreshTrigger} />
                  </Box>
                ) : (
                  <CodesManager refreshTrigger={refreshTrigger}/>
                )}
              </Box>
            </Box>
          ) : (
            // Если активного раздела нет — показываем список
            <List sx={{ 
              width: '100%',
              bgcolor: 'background.paper',
              mt: 1,
            }}>
              <AppBar  
                color="transparent" 
                elevation={0}
                sx={{ mb: 3, bgcolor: 'background.paper' }}
              >
                <Toolbar sx={{ px: 0, color: 'white' }}>
                  <Typography variant="h5" sx={{ ml: isMobile ? 7 : 32 }}>
                    Бош сахифа
                  </Typography>
                </Toolbar>
              </AppBar>

              <Box sx={{mt:6}}></Box>

              {sections.map((section) => (
                <ListItem 
                  key={section.id} 
                  disablePadding
                  sx={{
                    borderBottom: '1px solid #e9ecef',
                    '&:last-child': {
                      borderBottom: 'none'
                    }
                  }}
                >
                  <ListItemButton
                    onClick={() => setActiveSection(section.id)}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#c5c6c9',
                      },
                      color: 'black',
                      py: 2,
                      px: 3
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, color: 'black'}}>
                      {section.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={section.label} 
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
          <AddProductModal
            open={productModalOpen}
            product={editingProduct}
            onClose={handleModalClose}
          />
        </Container>
      </MainContent>
    </Box>
  );
}